export type ApplicationErrorCategory =
  "offline" | "unauthorized" | "invalid-data" | "stale-revision" | "unexpected";

export class ApplicationError extends Error {
  readonly category: ApplicationErrorCategory;

  constructor(
    category: ApplicationErrorCategory,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = "ApplicationError";
    this.category = category;
  }
}

export class InvalidDataError extends ApplicationError {
  readonly source: string;

  constructor(source: string, options?: ErrorOptions) {
    super("invalid-data", `Invalid ${source} data.`, options);
    this.name = "InvalidDataError";
    this.source = source;
  }
}

export class OfflineError extends ApplicationError {
  constructor(
    message = "Check your connection and try again.",
    options?: ErrorOptions,
  ) {
    super("offline", message, options);
    this.name = "OfflineError";
  }
}

export class UnauthorizedError extends ApplicationError {
  constructor(
    message = "You don't have permission to do that.",
    options?: ErrorOptions,
  ) {
    super("unauthorized", message, options);
    this.name = "UnauthorizedError";
  }
}

export class StaleRevisionError extends ApplicationError {
  constructor(
    message = "Someone else made a change first — try again.",
    options?: ErrorOptions,
  ) {
    super("stale-revision", message, options);
    this.name = "StaleRevisionError";
  }
}

export class UnexpectedApplicationError extends ApplicationError {
  constructor(
    message = "Something went wrong. Try again.",
    options?: ErrorOptions,
  ) {
    super("unexpected", message, options);
    this.name = "UnexpectedApplicationError";
  }
}

interface ErrorLike {
  code?: unknown;
  details?: { reason?: unknown };
}

export function normalizeApplicationError(
  error: unknown,
  message?: string,
): ApplicationError {
  if (error instanceof ApplicationError) return error;
  const candidate =
    typeof error === "object" && error !== null ? (error as ErrorLike) : {};
  const code =
    typeof candidate.code === "string"
      ? candidate.code.replace(/^functions\//, "")
      : "";
  const reason = candidate.details?.reason;
  const options = error instanceof Error ? { cause: error } : undefined;

  if (
    code === "unavailable" ||
    code === "network-request-failed" ||
    code === "deadline-exceeded"
  ) {
    return new OfflineError(message, options);
  }
  if (code === "permission-denied" || code === "unauthenticated") {
    return new UnauthorizedError(message, options);
  }
  if (code === "failed-precondition" && reason === "stale-revision") {
    return new StaleRevisionError(message, options);
  }
  return new UnexpectedApplicationError(message, options);
}
